import * as React from "react";
import {useContext, useEffect, useRef} from "react";
import {useTranslation} from "react-i18next";
import {AgGridReact} from "ag-grid-react";
import {ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams} from "ag-grid-enterprise";
import IconButton from "@mui/material/IconButton";
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import {STATE_SUMMARY_UPDATING} from "../../../constants/FinanceRecoil";
import {FilterDef} from "../../../constants/FinanceModels";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {DescriptionRounded} from "@mui/icons-material";
import {Tooltip, useTheme} from "@mui/material";
import {formatDate} from "../../../utils/FormatUtils";
import {getLocalGridState, setLocalGridState} from "../../../utils/GridUtils";
import FinanceDynamicActions from "../../../component/FinanceDynamicActions";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import {NumericSummary} from "../../../component/NumericSummary";
import {useAgGridLocale, useHasScope} from "../../../../../config/globalConfig";
import {ETOLL_VIEW_CHARGES} from "../../../utils/FinanceScope";
import {
    COLOR_IMPORTED,
    COLOR_LOADING,
    COLOR_PROCESSED,
    ID_IMPORTED,
    ID_LOADING,
    ID_PROCESSED,
    STATE_DISABLE_TAB_ETOLL,
    STATE_ETOLL_REPORT_ACTIVE_FILTERS,
    STATE_ETOLL_REPORT_TOTAL_COUNT,
    STATE_ETOLL_REPORT_UPDATING
} from "../constants/ETollConstants";
import {useETollReportRepository} from "../../../api/repositories/ETollReportRepository";
import ColouredChip from "../../../component/ColouredChip";

const ETollReportTable = () => {

    const UNIQUE_ID = "fnn-report-etoll";

    const {t} = useTranslation('finance', {keyPrefix: 'etoll'});
    const {getReports, getSummary} = useETollReportRepository();

    const {globalData: {user}} = useContext(GlobalContext);
    const hasScope = useHasScope;
    const setTotalCount = useSetRecoilState(STATE_ETOLL_REPORT_TOTAL_COUNT);

    const [updating, setUpdating] = useRecoilState(STATE_ETOLL_REPORT_UPDATING);
    const setSummaryUpdating = useSetRecoilState(STATE_SUMMARY_UPDATING);

    const reportFilterDefs = useRecoilValue(STATE_ETOLL_REPORT_ACTIVE_FILTERS);

    const [disableTab, setDisableTab] = useRecoilState(STATE_DISABLE_TAB_ETOLL);
    const theme = useTheme();
    const gridRef = useRef<AgGridReact>(null);

    const legalEntities: any[] = user.legalEntities?.filter((x: any) => x.name.toLowerCase() !== "all");
    const dealers: any[] = user.dealers?.filter((x: any) => x.name.toLowerCase() !== "all");

    const agGridLocale = useAgGridLocale();

    const columnDefs: ColDef[] = [
        {
            field: 'fileName',
            headerName: t('tables.report.fileName'),
            headerTooltip: t('tables.report.fileName'),
            suppressSizeToFit: true,
            minWidth: 250,
            unSortIcon: true,
            pinned: "left",
            lockPosition: "left",
            lockVisible: true,
            lockPinned: true
        },
        {
            field: 'fileCreationDate',
            headerName: t('tables.report.fileCreation'),
            headerTooltip: t('tables.report.fileCreation'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => formatDate(params.value)
        },
        {
            field: 'sequenceNumber',
            headerName: t('tables.report.sequenceNumber'),
            headerTooltip: t('tables.report.sequenceNumber'),
            minWidth: 200,
            unSortIcon: true
        },
        {
            field: 'previousSequenceNumber',
            headerName: t('tables.report.previousSequenceNumber'),
            headerTooltip: t('tables.report.previousSequenceNumber'),
            minWidth: 200,
            unSortIcon: true
        },
        {
            field: 'version',
            headerName: t('tables.report.version'),
            headerTooltip: t('tables.report.version'),
            minWidth: 200,
            unSortIcon: true
        },
        {
            field: 'rows',
            headerName: t('tables.report.rows'),
            headerTooltip: t('tables.report.rows'),
            minWidth: 200,
            unSortIcon: true
        },
        {
            field: 'status',
            headerName: t('tables.report.status'),
            headerTooltip: t('tables.report.status'),
            minWidth: 150, suppressSizeToFit: true,
            sortable: true, unSortIcon: true,
            lockPosition: "right", lockVisible: true, lockPinned: true, pinned: "right",
            cellRendererSelector: (params) => {
                const status = params.data.status;
                switch (status) {
                    case ID_LOADING:
                        return {
                            component: () => <ColouredChip name={ID_LOADING} color={COLOR_LOADING}/>
                        };
                    case ID_IMPORTED:
                        return {
                            component: () => <ColouredChip name={ID_IMPORTED} color={COLOR_IMPORTED}/>
                        };
                    case ID_PROCESSED:
                        return {
                            component: () => <ColouredChip name={ID_PROCESSED} color={COLOR_PROCESSED}/>
                        }
                    default:
                        break;
                }
            }
        },
        {
            field: "legalEntity", headerName: t("tables.report.legalEntity"),
            minWidth: 180,
            unSortIcon: true, sortable: true, hide: legalEntities?.length <= 1
        },
        {
            field: "dealer", headerName: t("tables.report.dealer"),
            minWidth: 180,
            unSortIcon: true, sortable: true, hide: dealers?.length <= 1
        },
        {
            field: '_actions', headerName: '',
            suppressSizeToFit: true, minWidth: 100, maxWidth: 110, sortable: false,
            pinned: 'right', lockPosition: 'right', lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {
                return {
                    component: () =>
                        <FinanceDynamicActions>
                            {hasScope(ETOLL_VIEW_CHARGES) &&
                                <IconButton disabled={params.data.status === ID_LOADING}
                                            sx={{color: theme.palette.accent.main}} onClick={() => {
                                    window.open('/finance/e-toll/report/' + params.data.id, "_blank")
                                }}>
                                    <Tooltip arrow title={t('tables.report.actions.tooltips.view')}>
                                        <DescriptionRounded/>
                                    </Tooltip>
                                </IconButton>
                            }
                        </FinanceDynamicActions>
                };
            },
            suppressFiltersToolPanel: true
        }
    ];

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['id'],
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: false,
        },
    };

    useEffect(() => {
        if (reportFilterDefs.length === 0) {
            setTotalCount(null);
        }
        gridRef.current?.api?.setServerSideDatasource(createDatasource(reportFilterDefs, gridRef.current.api));
        setSummaryUpdating(true);
    }, [reportFilterDefs])

    useEffect(() => {
        if (updating) {
            gridRef.current?.api?.setServerSideDatasource(createDatasource(reportFilterDefs, gridRef.current.api));
            setUpdating(false);
            setSummaryUpdating(true);
        }
    }, [updating])

    useEffect(() => {
        setSummaryUpdating(true);
    }, [agGridLocale]);

    const createDatasource = (filterDefs: FilterDef[], api: GridApi<any>) => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let startRow = params.request.startRow ? params.request.startRow : 0;
                let endRow = params.request.endRow ? params.request.endRow : 10;
                let sort = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].colId;
                let order = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].sort;

                getReports(startRow, endRow - startRow, sort, order, filterDefs)
                    .then(response => {
                        let result = response.result;
                        setTotalCount(result["total-amount"]);

                        if (result["total-amount"] === 0) {
                            api.showNoRowsOverlay();
                        } else {
                            api.hideOverlay();
                        }

                        params.success({
                            rowData: result.content,
                            rowCount: result["total-amount"],
                        })
                    })
                    .catch(() => params.fail());
            }
        };
    }

    function saveState() {
        setLocalGridState(UNIQUE_ID, {
            rows: gridRef.current!.api.paginationGetPageSize(),
            cols: gridRef.current!.columnApi.getColumnState()
        });
    }

    const toolbar = {
        renderInfo: () => (
            <NumericSummary query={() => getSummary(reportFilterDefs)} fields={[ID_LOADING, ID_IMPORTED, ID_PROCESSED]}
                            i18nPath={"etoll.counters.reports"}/>
        )
    };

    return (
        <>
            <AgGridCustomTable
                toolbar={toolbar}
                rowModelType='serverSide'
                paginationPageSize={getLocalGridState(UNIQUE_ID).rows} serverSideInfiniteScroll
                gridRef={gridRef} columnDefs={columnDefs} gridOptions={gridOptions}
                rowSelection={'multiple'}
                suppressRowClickSelection
                onGridReady={() => {
                    gridRef.current!.api.setServerSideDatasource(createDatasource(reportFilterDefs, gridRef.current!.api));

                    let gridState = getLocalGridState(UNIQUE_ID).cols;
                    if (gridState !== undefined) {
                        gridRef.current!.columnApi.applyColumnState({
                            state: gridState,
                            applyOrder: true
                        })
                    }
                }}
                onColumnMoved={e => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onColumnPinned={e => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onColumnResized={e => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onSortChanged={e => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onPaginationChanged={() => {
                    setLocalGridState(UNIQUE_ID, {
                        rows: gridRef.current!.api?.paginationGetPageSize(),
                        cols: getLocalGridState(UNIQUE_ID).cols
                    });
                }}
                onGridSizeChanged={() => gridRef.current!.api.sizeColumnsToFit()}
            />
        </>

    );
}

export default ETollReportTable;